import { useEffect, useRef, useState, type ReactNode } from "react";
import type { WordDTO } from "@shared/types";
import { shuffle } from "@shared/game";
import { speak, speakPromptOnce, type LoopHandle } from "../../audio";
import { GameImage } from "../GameImage";

export interface ChallengeProps {
  word: WordDTO;
  distractors: WordDTO[];
  onAnswer: (correct: boolean) => void;
}

interface ChoiceGridProps {
  choices: WordDTO[];
  correctId: string;
  picked: string | null;
  onPick: (c: WordDTO) => void;
  render: (c: WordDTO) => ReactNode;
  columns?: 2 | 3;
}

export function ChoiceGrid({
  choices,
  correctId,
  picked,
  onPick,
  render,
  columns = 2,
}: ChoiceGridProps) {
  return (
    <div
      className="grid"
      style={{
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap: 12,
        marginTop: 16,
      }}
    >
      {choices.map((c) => {
        const correct = c.id === correctId;
        const isPicked = picked === c.id;
        const cls = picked
          ? isPicked
            ? correct
              ? "good"
              : "bad"
            : correct
              ? "good faded"
              : "faded"
          : "";
        return (
          <button
            key={c.id}
            className={`choice ${cls}`}
            onClick={() => onPick(c)}
            disabled={!!picked}
          >
            {render(c)}
          </button>
        );
      })}
    </div>
  );
}

export function useChoices(word: WordDTO, distractors: WordDTO[]) {
  // Stable per (wordId, distractor ids) so order doesn't reshuffle on render.
  const key = `${word.id}:${distractors.map((d) => d.id).join(",")}`;
  const ref = useRef<{ key: string; value: WordDTO[] } | null>(null);
  if (!ref.current || ref.current.key !== key) {
    ref.current = { key, value: shuffle([word, ...distractors.slice(0, 3)]) };
  }
  return ref.current.value;
}

export function useAudioPrompt(
  text: string,
  opts: { audioUrl?: string | null } = {},
) {
  const ref = useRef<LoopHandle | null>(null);
  const audioUrl = opts.audioUrl ?? null;
  useEffect(() => {
    if (!text) return;
    ref.current = speakPromptOnce(text, { audioUrl });
    return () => ref.current?.cancel();
  }, [text, audioUrl]);
  return {
    replay: () => {
      if (text) speak(text, { audioUrl });
    },
    cancel: () => ref.current?.cancel(),
  };
}

interface ShellProps {
  tag: string;
  prompt?: string;
  imageUrl?: string | null;
  speakerText?: string;
  speakerAudioUrl?: string | null;
  children: ReactNode;
}

export function ChallengeImage({
  src,
  alt = "",
  className = "challenge-image",
}: {
  src?: string | null;
  alt?: string;
  className?: string;
}) {
  return <GameImage className={className} src={src} alt={alt} />;
}

export function ChallengeShell({
  tag,
  prompt,
  imageUrl,
  speakerText,
  speakerAudioUrl,
  children,
}: ShellProps) {
  const speaker = useAudioPrompt(speakerText ?? "", {
    audioUrl: speakerAudioUrl,
  });

  return (
    <div className="challenge-overlay">
      <div className="challenge-card">
        <div className="challenge-header">
          <span className="challenge-tag">{tag}</span>
          {speakerText && (
            <button
              className="btn btn-blue btn-pill"
              onClick={speaker.replay}
            >
              🔊 Replay
            </button>
          )}
        </div>
        {imageUrl && <ChallengeImage src={imageUrl} />}
        {prompt && <h2 className="challenge-prompt">{prompt}</h2>}
        {children}
      </div>
    </div>
  );
}

/**
 * Handle a pick with feedback delay. On a wrong answer, opens a short teaching
 * moment and says the correct word once.
 */
export function usePickHandler(word: WordDTO, onAnswer: (correct: boolean) => void) {
  const [picked, setPicked] = useState<string | null>(null);
  const [teaching, setTeaching] = useState(false);
  const timers = useRef<number[]>([]);

  useEffect(() => {
    return () => {
      timers.current.forEach(window.clearTimeout);
      timers.current = [];
    };
  }, []);

  const later = (fn: () => void, ms: number) => {
    const timer = window.setTimeout(fn, ms);
    timers.current.push(timer);
  };

  const handle = (c: WordDTO) => {
    if (picked) return;
    setPicked(c.id);
    const correct = c.id === word.id;
    if (correct) {
      later(() => onAnswer(true), 800);
      return;
    }
    // Wrong → teach moment.
    later(() => setTeaching(true), 600); // brief beat to register the bad X feedback
    later(() => speak(word.english, { audioUrl: word.audioUrl }), 900);
    later(() => onAnswer(false), 3600);
  };

  const teachOverlay = teaching ? <TeachOverlay word={word} /> : null;
  return { picked, handle, teachOverlay };
}

function TeachOverlay({ word }: { word: WordDTO }) {
  return (
    <div className="teach-overlay">
      <div className="teach-card">
        <p className="muted teach-label">The answer was…</p>
        {word.imageUrl && <ChallengeImage className="teach-image" src={word.imageUrl} alt={word.english} />}
        <h2 className="teach-english">{word.english}</h2>
        <div className="teach-progress" />
      </div>
    </div>
  );
}
